import * as React from 'react'

import { TFCartFullItem, TFProductInList, TPromoTagEnum } from '~graphql/generated/clientGraphql'
import { createContext } from '~logic/lib/react'

type TCartItem = TFCartFullItem
type TExtendedCartItem = TCartItem['product'] & Partial<TFProductInList>
type TExtendedProduct = TFProductInList & Partial<TCartItem['product']>

type TOpenProductOptions = {
  product: TExtendedProduct
  promoTag: TProductContext['promoTag']
  categoryId: Defined<TProductContext['categoryId']>
}

type TOpenCartItemOptions = {
  product: TExtendedCartItem
  cartItemIds: Defined<TProductContext['editedCartItemIds']>
}

type TControlContext = {
  closeProductDetail: () => void
  openProductDetail: (options: TOpenProductOptions) => void
  openCartItemDetail: (options: TOpenCartItemOptions) => void
}

type TProductContext = {
  isEditingItem: boolean
  product: Nullable<TExtendedProduct | TExtendedCartItem>
  // if the detail is open form a Cart edit button, the customer is editing existing Cart/Order items (can be grouped)
  // therefore the configured item(s) will be updated and not added to the cart (different mutation will be called)
  // we need IDs of those items to know what should the M be called with
  editedCartItemIds: Nullable<RoA<ID>>
  promoTag: Nullable<TPromoTagEnum>
  categoryId: Nullable<ID>
}

const [ProductProvider, useProductDetailCtx] = createContext<TProductContext>(`ProductDetail`)
const [ControlProvider, useProductDetailControlCtx] =
  createContext<TControlContext>(`ProductDetailControl`)

type TProps = RequiredChildren

/**
 * Handles selecting a product (from menu list or cart items) for the product detail modal.
 * use the `useProductDetail` hook to access the data needed by the ProductDetail (currently selected product etc.)
 * use the `useProductDetailControl` hook to get functions for controlling the detail (modal/drawer) like open/close
 */
export const ProductDetailProvider: React.FC<TProps> = ({ children }) => {
  const [product, setProduct] = React.useState<TProductContext['product']>(null)
  const [promoTag, setPromoTag] = React.useState<TProductContext['promoTag']>(null)
  const [categoryId, setCategoryId] = React.useState<TProductContext['categoryId']>(null)
  const [editedCartItemIds, setEditedCartItemIds] =
    React.useState<TProductContext['editedCartItemIds']>(null)

  const openProductDetail = React.useCallback<TControlContext['openProductDetail']>(
    ({ product, promoTag, categoryId }) => {
      setProduct(product)
      setPromoTag(promoTag)
      setCategoryId(categoryId)
    },
    [],
  )

  const openCartItemDetail = React.useCallback<TControlContext['openCartItemDetail']>(
    ({ product, cartItemIds }) => {
      setProduct(product)
      setPromoTag(null)
      setCategoryId(null)
      setEditedCartItemIds(cartItemIds)
    },
    [],
  )

  const closeProductDetail = React.useCallback<TControlContext['closeProductDetail']>(() => {
    setProduct(null)
    setPromoTag(null)
    setCategoryId(null)
    setEditedCartItemIds(null)
  }, [])

  const isEditingItem = React.useMemo(() => Boolean(editedCartItemIds), [editedCartItemIds])

  return (
    <ControlProvider
      value={{
        openProductDetail,
        openCartItemDetail,
        closeProductDetail,
      }}
    >
      <ProductProvider
        value={{
          product,
          promoTag,
          categoryId,
          isEditingItem,
          editedCartItemIds,
        }}
      >
        {children}
      </ProductProvider>
    </ControlProvider>
  )
}

export { useProductDetailCtx, useProductDetailControlCtx }
